/**
 * Cases the courts have gone quiet on.
 *
 * Lloyd, Aug 19: *"I think the general rule should be 45 days of silence, the court gets an email
 * asking for a status update."* This is the review screen that comes before any of that is sent.
 *
 * ## It drafts; it does not send
 *
 * The button writes Outlook drafts into the Court Communication mailbox and stamps each case as
 * asked. **There is still no send button anywhere**, `stale_case.court_autosend` is off, and the
 * route behind this screen has no path that delivers mail. Somebody opens the mailbox and presses
 * send, which is the point: this is the highest-volume outbound path in the system and it is aimed
 * at courts.
 *
 * Each row shows the letter that case would actually get, built by the same two functions the draft
 * run uses. A preview assembled any other way could drift from what is written, which would make
 * the review worse than no review at all.
 *
 * ## Three numbers, kept apart
 *
 * **Waiting** is how many cases match. **Shown** is how many this batch covers — the cap is a
 * review limit, and the oldest bucket runs past a thousand cases, at which point "a person reviews
 * the batch" stops meaning anything. **No court email** is different again: those cases are not
 * waiting on us at all, they cannot be chased until somebody fills in the court record, and they
 * are the oldest cases in the firm. Folding them into one total would bury a data problem inside a
 * workload number.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

interface StaleCaseRow {
  ticketGuid: string;
  ticketNumber: string | null;
  ageDays: number;
  daysSilent: number;
  courtName: string | null;
  courtEmail: string | null;
  variant: 'status-request' | 'closing-paperwork' | 'setting-request';
  hasUpcomingSetting: boolean;
  explanation: string;
  lastContactAt: string | null;
  lastContactSource: 'court_email' | 'offer' | 'manual' | null;
  subject: string;
  body: string;
}

interface ExcludedCase {
  ticketGuid: string;
  ticketNumber: string | null;
  courtName: string | null;
  reason: 'no-court-email' | 'already-asked';
}

/** How we know the court was last in touch. A day count without its evidence is not reviewable. */
const CONTACT_SOURCE_LABELS: Record<NonNullable<StaleCaseRow['lastContactSource']>, string> = {
  court_email: 'email from the court',
  offer: 'offer from the court',
  manual: 'recorded by staff',
};

const EXCLUSION_LABELS: Record<ExcludedCase['reason'], string> = {
  'no-court-email': 'No usable court email',
  'already-asked': 'Already asked, not yet answered',
};

/**
 * The draft body as text.
 *
 * Rendered as text rather than HTML on purpose: the body is assembled from a citation number and a
 * client name, both free text out of the database, and putting that through innerHTML would make a
 * ticket number an injection point on a staff screen.
 */
function draftLines(html: string): string[] {
  return html
    .split(/<\/p>/i)
    .map((part) => part.replace(/<[^>]*>/g, '').trim())
    .filter((part) => part !== '');
}

/** What each wording actually asks the clerk for, so a reviewer can group a batch by eye. */
const VARIANT_LABELS: Record<StaleCaseRow['variant'], string> = {
  'closing-paperwork': 'Asks for closing paperwork',
  'setting-request': 'Asks whether it has been set',
  'status-request': 'Asks for a status update',
};

interface StaleCaseResponse {
  stageMonths: number;
  stages: number[];
  matchedTotal: number;
  droppedToCap: number;
  withoutCourtEmail: number;
  alreadyAsked: number;
  excluded: ExcludedCase[];
  summary: string;
  cases: StaleCaseRow[];
}

interface DraftRunResponse {
  drafted: number;
  draftFailed: number;
  stampFailed: number;
  /** On screen at review time but no longer silent by the time the button was pressed. */
  noLongerQualifying: number;
  summary: string;
}

declare const process: { env: { API_HOST?: string } };

function ManagerStaleCasesView() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState<StaleCaseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDraft, setOpenDraft] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [draftResult, setDraftResult] = useState<DraftRunResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /**
   * Which cases the button will write to (Hannah, 16 Sep: "Is there a way to draft an email for an
   * individual case rather than drafting 100?").
   *
   * Starts empty rather than pre-selected. The button writes into a shared mailbox and stamps every
   * case it touches, and neither can be undone from here, so the safe default is that pressing it
   * with nothing chosen does nothing at all.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggleSelected = (ticketGuid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(ticketGuid)) next.add(ticketGuid);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOpenDraft(null);
    // A guid chosen in one bucket may not be in the next one, and a stamped case drops off the list
    // entirely after a run. Carrying the old choices over would send guids nobody can still see.
    setSelected(new Set());
    try {
      const host = process.env.API_HOST?.trim() || window.location.origin;
      const response = await fetch(
        `${host}/api/v1/manager/stale-cases?months=${months}`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load the stale-case list');
      }
      setData(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the stale-case list');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Write the batch as drafts. Nothing is delivered.
   *
   * The ids of the rows actually on screen are sent, not just the filter: between this list
   * rendering and the button being pressed a court can reply, an offer can land, or a colleague can
   * draft the same batch. Sending the filter alone would write letters nobody reviewed. The server
   * intersects them with a fresh plan, so a case that has since been answered is dropped and
   * counted rather than written to.
   *
   * Reloaded afterwards because each drafted case is stamped as asked and drops out of the next
   * batch, so a stale list would invite somebody to press it twice and write to the same clerk
   * twice.
   */
  const createDrafts = async () => {
    if (!data) return;
    setConfirmOpen(false);
    setDrafting(true);
    setError(null);
    setDraftResult(null);
    try {
      const host = process.env.API_HOST?.trim() || window.location.origin;
      const response = await fetch(`${host}/api/v1/manager/stale-cases/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          months,
          // Only the chosen rows, and only ones still on screen, so a guid left over from an
          // earlier list cannot be written to.
          ticketGuids: data.cases
            .map((row) => row.ticketGuid)
            .filter((guid) => selected.has(guid)),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || 'Failed to create the drafts');
      setDraftResult(payload);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the drafts');
    } finally {
      setDrafting(false);
    }
  };

  const confirmDialog = (
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Draft {selected.size} letter(s) to courts?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          One draft per case goes into the Court Communication mailbox, and each of these cases is
          marked as asked so it drops out of the next batch.
        </Typography>
        <Typography variant="body2">
          Nothing is sent. Somebody still has to open the mailbox and press send on each one. The
          drafts and the stamps cannot be undone from here.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={createDrafts}>
          Draft them
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      {confirmDialog}
      <Typography variant="h5" sx={{ mb: 1 }}>
        Cases waiting on a court
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open cases where the court has not been in touch for 45 days. Nothing here has been sent —
        this is the list to work through, oldest silence first.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={months}
        onChange={(_, value) => setMonths(value)}
        sx={{ mb: 2 }}
      >
        {(data?.stages ?? [6, 5, 4, 3]).map((stage) => (
          <Tab key={stage} value={stage} label={`${stage}+ months`} />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : !data ? null : (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`${data.matchedTotal} waiting`} />
            <Chip label={`${data.cases.length} shown`} variant="outlined" />
            {/* Named rather than hidden: a batch that quietly covered a third of what it claimed
                would be worse than one that says so. */}
            {data.droppedToCap > 0 && (
              <Chip
                color="warning"
                variant="outlined"
                label={`${data.droppedToCap} not covered by this batch`}
              />
            )}
            {data.withoutCourtEmail > 0 && (
              <Chip
                color="error"
                variant="outlined"
                label={`${data.withoutCourtEmail} have no court email`}
              />
            )}
            {data.alreadyAsked > 0 && (
              <Chip variant="outlined" label={`${data.alreadyAsked} already asked`} />
            )}
          </Stack>

          {data.withoutCourtEmail > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {data.withoutCourtEmail} of these cases cannot be chased at all, because their court
              has no usable email address on file. They are among the oldest cases we hold. The
              court record needs filling in before anything else will reach them.
            </Alert>
          )}

          {draftResult && (
            <Alert
              severity={draftResult.draftFailed > 0 || draftResult.stampFailed > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
              onClose={() => setDraftResult(null)}
            >
              {draftResult.summary}
              {/* Named rather than quietly dropped: those cases were on screen when the batch was
                  approved and were not written to. */}
              {draftResult.noLongerQualifying > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {draftResult.noLongerQualifying} case(s) on that list had been answered or drafted
                  since you looked, so nothing was written to them.
                </Typography>
              )}
            </Alert>
          )}

          {data.cases.length > 0 && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              {/*
                Confirmed first. One press writes up to a hundred drafts into a shared mailbox
                through Graph and stamps every one of those cases as asked, which takes them out of
                the next batch. Nothing sends, but nothing about that is undoable either.
              */}
              <Button
                variant="contained"
                onClick={() => setConfirmOpen(true)}
                disabled={drafting || selected.size === 0}
              >
                {drafting ? 'Drafting…' : `Draft the ${selected.size} selected`}
              </Button>
              <Button
                size="small"
                onClick={() =>
                  setSelected(
                    selected.size === data.cases.length
                      ? new Set()
                      : new Set(data.cases.map((row) => row.ticketGuid)),
                  )
                }
              >
                {selected.size === data.cases.length ? 'Clear all' : `Select all ${data.cases.length}`}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Writes drafts to the Court Communication mailbox and marks each case as asked.
                Nothing is sent — somebody opens the mailbox and presses send.
              </Typography>
            </Stack>
          )}

          {data.cases.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nothing is waiting in this bucket.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {data.cases.map((row) => (
                <Paper key={row.ticketGuid} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    <Checkbox
                      size="small"
                      sx={{ p: 0.5 }}
                      checked={selected.has(row.ticketGuid)}
                      onChange={() => toggleSelected(row.ticketGuid)}
                      inputProps={{
                        'aria-label': `Draft a letter for ${row.ticketNumber ?? 'this case'}`,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: 140 }}>
                      {row.ticketNumber || row.ticketGuid.slice(0, 8)}
                    </Typography>
                    <Chip size="small" label={`${row.daysSilent}d silent`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={VARIANT_LABELS[row.variant] ?? 'Asks for a status update'}
                    />
                    {!row.hasUpcomingSetting && (
                      // Worth seeing even on the older cases, which get the closing-paperwork
                      // wording: a stale case with nothing on the calendar is a different problem
                      // from one that is merely quiet.
                      <Chip size="small" color="warning" variant="outlined" label="No setting" />
                    )}
                    {!row.courtEmail && (
                      <Chip size="small" color="error" variant="outlined" label="No court email" />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {row.courtName ?? 'No court on the case'} · {row.explanation}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {row.lastContactAt
                      ? `Last heard from ${new Date(row.lastContactAt).toLocaleDateString('en-US')} (${
                          row.lastContactSource
                            ? CONTACT_SOURCE_LABELS[row.lastContactSource]
                            : 'source not recorded'
                        })`
                      : 'The court has never been recorded as making contact on this case.'}
                  </Typography>

                  <Button
                    size="small"
                    sx={{ mt: 0.5, px: 0 }}
                    onClick={() =>
                      setOpenDraft(openDraft === row.ticketGuid ? null : row.ticketGuid)
                    }
                  >
                    {openDraft === row.ticketGuid ? 'Hide the letter' : 'Read the letter'}
                  </Button>

                  <Collapse in={openDraft === row.ticketGuid}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary" display="block">
                      To {row.courtEmail ?? 'no address on file'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                      {row.subject}
                    </Typography>
                    {draftLines(row.body).map((line, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                        {line}
                      </Typography>
                    ))}
                  </Collapse>
                </Paper>
              ))}
            </Stack>
          )}

          {data.excluded.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Matched, but not in this batch
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                These cases are silent too. Nothing will be written to them until the reason below
                is dealt with.
              </Typography>
              <Stack spacing={1}>
                {data.excluded.map((row) => (
                  <Paper key={row.ticketGuid} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexWrap: 'wrap', gap: 1 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: 140 }}>
                        {row.ticketNumber || row.ticketGuid.slice(0, 8)}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={row.reason === 'no-court-email' ? 'error' : 'default'}
                        label={EXCLUSION_LABELS[row.reason]}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {row.courtName ?? 'No court on the case'}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default ManagerStaleCasesView;
